/**
 * Device profile system — classifies the current device into a size tier
 * based on screen dimensions and OS, used by all mobile UI components
 * to adjust sizing and layout.
 *
 * Why profiles instead of model lookup:
 *   - Thousands of phone models, impossible to maintain
 *   - Screen dimensions + OS are the actual variables that matter
 *   - Works for future phones (iPhone 17, 18, ...) automatically
 */

export type DeviceTier = 'small-phone' | 'phone' | 'large-phone' | 'tablet';

export interface DeviceProfile {
  tier: DeviceTier;
  os: 'ios' | 'android' | 'other';
  /** CSS pixel width */
  width: number;
  /** CSS pixel height */
  height: number;
  /** Device pixel ratio */
  dpr: number;
  /** Bottom safe area inset (px), for notched iPhones */
  safeBottom: number;
  /** Whether the device has a physical notch / dynamic island */
  hasNotch: boolean;

  // Derived sizing (computed from tier)
  bottomBarHeight: number;
  buildingButtonSize: number;
  buildingButtonGap: number;
  actionButtonSize: number;
  actionButtonGap: number;
  hudFontSize: number;
  barFontSize: number;
}

let cachedProfile: DeviceProfile | null = null;

export function getDeviceProfile(): DeviceProfile {
  if (cachedProfile) return cachedProfile;

  const w = window.innerWidth;
  const h = window.innerHeight;
  const ua = navigator.userAgent;
  const dpr = window.devicePixelRatio || 1;

  // OS detection
  let os: DeviceProfile['os'] = 'other';
  if (/iPhone|iPad|iPod/.test(ua)) os = 'ios';
  else if (/Android/.test(ua)) os = 'android';

  // Tier by width (CSS pixels)
  let tier: DeviceTier;
  if (w < 380) {
    tier = 'small-phone';          // iPhone SE (375), older Androids
  } else if (w < 430) {
    tier = 'phone';                // iPhone 12-16 (390-393), standard Androids (~412)
  } else if (w < 600) {
    tier = 'large-phone';          // iPhone Pro Max (430-440), large Androids
  } else {
    tier = 'tablet';               // iPad, Android tablets, foldables unfolded
  }

  // Safe area (iOS Safari uses env() but JS can read it via CSS custom properties)
  let safeBottom = 0;
  try {
    const val = getComputedStyle(document.documentElement).getPropertyValue('--safe-bottom');
    if (val) safeBottom = parseInt(val, 10) || 0;
  } catch { /* ignore */ }

  // Notch detection: iOS + dpr >= 3 + safe area or edge-to-edge
  const hasNotch = os === 'ios' && dpr >= 3;

  // Derived sizes by tier
  const sizing = TIER_SIZING[tier];

  cachedProfile = {
    tier, os, width: w, height: h, dpr, safeBottom, hasNotch,
    ...sizing,
  };
  return cachedProfile;
}

/** Clear cached profile (call on resize/orientation change). */
export function resetDeviceProfile(): void {
  cachedProfile = null;
}

interface TierSizing {
  bottomBarHeight: number;
  buildingButtonSize: number;
  buildingButtonGap: number;
  actionButtonSize: number;
  actionButtonGap: number;
  hudFontSize: number;
  barFontSize: number;
}

const TIER_SIZING: Record<DeviceTier, TierSizing> = {
  'small-phone': {
    bottomBarHeight: 44,
    buildingButtonSize: 34,
    buildingButtonGap: 2,
    actionButtonSize: 38,
    actionButtonGap: 4,
    hudFontSize: 10,
    barFontSize: 13,
  },
  'phone': {
    bottomBarHeight: 50,
    buildingButtonSize: 40,
    buildingButtonGap: 3,
    actionButtonSize: 42,
    actionButtonGap: 5,
    hudFontSize: 11,
    barFontSize: 14,
  },
  'large-phone': {
    bottomBarHeight: 56,
    buildingButtonSize: 48,
    buildingButtonGap: 4,
    actionButtonSize: 48,
    actionButtonGap: 6,
    hudFontSize: 12,
    barFontSize: 16,
  },
  'tablet': {
    bottomBarHeight: 64,
    buildingButtonSize: 56,
    buildingButtonGap: 6,
    actionButtonSize: 56,
    actionButtonGap: 8,
    hudFontSize: 14,
    barFontSize: 18,
  },
};
